import React, { useState, useEffect } from 'react';
import * as XLSX from 'xlsx';
import { API_URL } from '../config';

function SimpleCAImport({ onClose, isDarkMode }) {
  const [file, setFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState('');
  const [results, setResults] = useState(null);
  const [previewData, setPreviewData] = useState(null);
  const [showPreview, setShowPreview] = useState(false);

  // Debug: sledování změn v previewData
  useEffect(() => {
    if (previewData) {
      console.log('=== PREVIEW DATA CHANGED (useEffect) ===');
      console.log('Preview data:', previewData);
      console.log('First item CA data:', previewData.preview?.[0]?.changes?.ca);
    }
  }, [previewData]);

  const handleFileSelect = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setResults(null);
      setMessage('');
      setPreviewData(null);
      setShowPreview(false);
    }
  };

  const generatePreview = async () => {
    if (!file) {
      alert('Vyberte prosím Excel soubor');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setMessage('Načítám Excel soubor...');

    try {
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          if (jsonData.length < 2) {
            throw new Error('Excel soubor musí obsahovat minimálně hlavičky a jeden řádek dat');
          }

          setMessage('Načítám existující společnosti...');
          setProgress(30);

          // Získej všechna IČO z Excel souboru
          // Debug: zobrazit první řádek (hlavičky)
          console.log('Excel hlavičky:', jsonData[0]);

          const excelData = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length < 3) continue;

            const ico = String(row[0] || '').replace(/[^\d]/g, '');
            const ca = String(row[1] || '').trim();
            const prm = String(row[2] || '').trim();

            // Debug pro první 3 řádky
            if (i <= 3) {
              console.log(`Excel řádek ${i}:`, {
                raw_row: row,
                ico: ico,
                ca: `"${ca}"`,
                ca_length: ca.length,
                prm: `"${prm}"`,
                prm_length: prm.length
              });
            }

            if (ico.length >= 6) {
              excelData.push({ ico, ca, prm });
            }
          }

          if (excelData.length === 0) {
            throw new Error('Nenašly se žádné platné řádky s IČO');
          }

          setMessage('Hledám společnosti v databázi...');
          setProgress(60);

          // Najdi společnosti v databázi podle IČO (v dávkách)
          const companies = [];
          const batchSize = 500; // Menší dávky kvůli výkonu
          
          for (let i = 0; i < excelData.length; i += batchSize) {
            const batch = excelData.slice(i, i + batchSize);
            const icos = batch.map(item => item.ico);
            
            const response = await fetch(`${API_URL}/companies/by-ico`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('token')}`
              },
              body: JSON.stringify({ icos })
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Chyba při načítání společností: ${response.status} - ${errorText}`);
            }

            const batchCompanies = await response.json();
            companies.push(...batchCompanies);
          }

          // Vytvoř náhled změn
          const preview = [];
          let matchCount = 0;
          
          excelData.forEach(excelItem => {
            const company = companies.find(c => 
              c.ico && c.ico.replace(/[^\d]/g, '') === excelItem.ico
            );
            
            if (company) {
              matchCount++;
              // Debug: logování porovnání CA
              console.log(`CA porovnání pro ${company.name}:`, {
                company_ca: company.ca,
                company_ca_type: typeof company.ca,
                excel_ca: excelItem.ca,
                excel_ca_type: typeof excelItem.ca,
                are_different: company.ca !== excelItem.ca
              });

              const previewItem = {
                ico: excelItem.ico,
                name: company.name,
                changes: {
                  ca: {
                    old: company.ca || '(prázdné)',
                    new: excelItem.ca || '(prázdné)',
                    changed: company.ca !== excelItem.ca
                  },
                  prm: {
                    old: company.prm || '(prázdné)',
                    new: excelItem.prm || '(prázdné)',
                    changed: company.prm !== excelItem.prm
                  }
                }
              };

              // Debug: logování preview objektu
              console.log(`Preview objekt pro ${company.name}:`, previewItem);
              
              preview.push(previewItem);
            }
          });

          // Deep copy pro zabránění změn dat
          const finalPreviewData = {
            totalExcel: excelData.length,
            matchedCompanies: matchCount,
            preview: JSON.parse(JSON.stringify(preview.slice(0, 50))) // Deep copy prvních 50
          };

          // Debug: logování final preview dat
          console.log('=== FINAL PREVIEW DATA (BEFORE setState) ===');
          console.log('Total Excel:', finalPreviewData.totalExcel);
          console.log('Matched Companies:', finalPreviewData.matchedCompanies);
          console.log('Preview items count:', finalPreviewData.preview.length);
          console.log('First 3 preview items with CA:', finalPreviewData.preview.slice(0, 3).map(item => ({
            name: item.name,
            ca_old: item.changes.ca.old,
            ca_new: item.changes.ca.new,
            ca_changed: item.changes.ca.changed
          })));

          setPreviewData(finalPreviewData);

          setProgress(100);
          setMessage(`✅ Náhled připraven! Nalezeno ${matchCount} společností z ${excelData.length} řádků`);
          setShowPreview(true);
          setIsProcessing(false);

        } catch (error) {
          console.error('❌ Chyba při generování náhledu:', error);
          setMessage(`❌ Chyba: ${error.message}`);
          setIsProcessing(false);
        }
      };

      reader.readAsArrayBuffer(file);

    } catch (error) {
      console.error('❌ Chyba při čtení souboru:', error);
      setMessage(`❌ Chyba při čtení souboru: ${error.message}`);
      setIsProcessing(false);
    }
  };

  const confirmImport = async () => {
    if (!previewData) return;

    setIsProcessing(true);
    setProgress(0);
    setMessage('Připravuji import...');

    try {
      // Načti Excel znovu pro import
      const reader = new FileReader();
      
      reader.onload = async (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const worksheet = workbook.Sheets[workbook.SheetNames[0]];
          const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

          const updates = [];
          for (let i = 1; i < jsonData.length; i++) {
            const row = jsonData[i];
            if (!row || row.length < 3) continue;

            const ico = String(row[0] || '').replace(/[^\d]/g, '');
            const ca = String(row[1] || '').trim();
            const prm_vodafone = String(row[2] || '').trim(); // Backend očekává prm_vodafone

            // Debug: logování dat z Excelu
            if (i <= 3) { // Log prvních 3 řádků pro debug
              console.log(`Řádek ${i}:`, {
                originalRow: row,
                ico,
                ca: `"${ca}"`,
                prm_vodafone: `"${prm_vodafone}"`
              });
            }

            if (ico.length >= 6) {
              updates.push({ ico, ca, prm_vodafone });
            }
          }

          setMessage(`Importuji ${updates.length} společností...`);
          setProgress(50);

          const response = await fetch(`${API_URL}/companies/bulk-ca-update`, {
            method: 'PUT',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${localStorage.getItem('token')}`
            },
            body: JSON.stringify({ updates })
          });

          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Chyba serveru: ${response.status} - ${errorText}`);
          }

          const result = await response.json();
          
          setProgress(100);
          setMessage('✅ Import dokončen!');
          setResults(result);
          setShowPreview(false);
          setIsProcessing(false);
          
        } catch (error) {
          console.error('❌ Chyba při importu:', error);
          setMessage(`❌ Chyba: ${error.message}`);
          setIsProcessing(false);
        }
      };

      reader.readAsArrayBuffer(file);

    } catch (error) {
      console.error('❌ Chyba při importu:', error);
      setMessage(`❌ Chyba při importu: ${error.message}`);
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className={`${isDarkMode ? 'bg-gray-800 text-white' : 'bg-white text-gray-800'} rounded-lg shadow-xl max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto p-6`}>
        
        {/* Header */}
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-2xl font-bold">📊 Jednoduchý CA/PRM Import</h2>
          <button
            onClick={onClose}
            className={`${isDarkMode ? 'text-gray-300 hover:text-white' : 'text-gray-500 hover:text-gray-700'}`}
          >
            ✕
          </button>
        </div>

        {/* Instrukce */}
        <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-gray-100'} rounded p-4 mb-6`}>
          <h3 className="font-semibold mb-2">📋 Požadovaný formát Excel souboru:</h3>
          <ul className="text-sm space-y-1">
            <li><strong>Sloupec A:</strong> IČO (číslo společnosti)</li>
            <li><strong>Sloupec B:</strong> CA (hodnota pro CA pole)</li>
            <li><strong>Sloupec C:</strong> PRM (hodnota pro PRM pole)</li>
          </ul>
          <p className="text-xs mt-2 opacity-75">
            💡 První řádek může obsahovat hlavičky - budou přeskočeny
          </p>
        </div>

        {/* File upload */}
        <div className="mb-6">
          <label className="block text-sm font-medium mb-2">Vyberte Excel soubor:</label>
          <input
            type="file"
            accept=".xlsx,.xls"
            onChange={handleFileSelect}
            className={`w-full p-2 border rounded-lg ${
              isDarkMode 
                ? 'bg-gray-700 border-gray-600 text-white' 
                : 'bg-white border-gray-300'
            }`}
            disabled={isProcessing}
          />
        </div>

        {/* Progress */}
        {isProcessing && (
          <div className="mb-6">
            <div className="flex justify-between text-sm mb-2">
              <span>{message}</span>
              <span>{progress}%</span>
            </div>
            <div className={`w-full h-2 rounded-full ${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
              <div 
                className="h-2 bg-blue-500 rounded-full transition-all duration-300"
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}

        {/* Preview */}
        {showPreview && previewData && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-blue-50'} rounded p-4 mb-6`}>
            <h3 className="font-semibold text-blue-600 mb-4">👀 Náhled změn</h3>
            <div className="text-sm mb-4">
              <p><strong>Celkem řádků v Excelu:</strong> {previewData.totalExcel}</p>
              <p><strong>Nalezeno společností:</strong> {previewData.matchedCompanies}</p>
              <p className="text-xs opacity-75">Zobrazeno prvních 50 záznamů</p>
            </div>
            
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full text-xs border-collapse">
                <thead>
                  <tr className={`${isDarkMode ? 'bg-gray-600' : 'bg-gray-200'}`}>
                    <th className="border p-2">IČO</th>
                    <th className="border p-2">Název společnosti</th>
                    <th className="border p-2">CA změna</th>
                    <th className="border p-2">PRM změna</th>
                  </tr>
                </thead>
                <tbody>
                  {previewData.preview.map((item, index) => {
                    // Debug: logování každé položky při renderování
                    if (index < 3) {
                      console.log(`RENDER řádek ${index}:`, {
                        ico: item.ico,
                        name: item.name,
                        ca_changed: item.changes.ca.changed,
                        ca_old: item.changes.ca.old,
                        ca_new: item.changes.ca.new,
                        full_item: item
                      });
                    }

                    return (
                      <tr key={index} className={index % 2 === 0 ? (isDarkMode ? 'bg-gray-800' : 'bg-white') : (isDarkMode ? 'bg-gray-700' : 'bg-gray-50')}>
                        <td className="border p-2">{item.ico}</td>
                        <td className="border p-2">{item.name}</td>
                        <td className={`border p-2 ${item.changes.ca.changed ? 'text-orange-600' : 'text-gray-500'}`}>
                          {/* Debug inline */}
                          {console.log(`INLINE JSX DEBUG pro ${item.name}: changed=${item.changes.ca.changed}, old="${item.changes.ca.old}", new="${item.changes.ca.new}"`)}
                          
                          {(item.changes.ca.changed || item.changes.ca.old !== item.changes.ca.new) ? (
                          <span>
                            <span className="line-through">{item.changes.ca.old}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold">{item.changes.ca.new}</span>
                          </span>
                        ) : (
                          <span>Beze změny (DEBUG: old="{item.changes.ca.old}" new="{item.changes.ca.new}")</span>
                        )}
                      </td>
                      <td className={`border p-2 ${item.changes.prm.changed ? 'text-green-600' : 'text-gray-500'}`}>
                        {item.changes.prm.changed ? (
                          <span>
                            <span className="line-through">{item.changes.prm.old}</span>
                            <span className="mx-1">→</span>
                            <span className="font-bold">{item.changes.prm.new}</span>
                          </span>
                        ) : (
                          <span>Beze změny</span>
                        )}
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Results */}
        {results && (
          <div className={`${isDarkMode ? 'bg-gray-700' : 'bg-green-50'} rounded p-4 mb-6`}>
            <h3 className="font-semibold text-green-600 mb-2">✅ Výsledky importu:</h3>
            <div className="text-sm space-y-1">
              <p><strong>Aktualizováno:</strong> {results.updatedCount} společností</p>
              <p><strong>Celkem zpracováno:</strong> {results.totalRequested} řádků</p>
              {results.errors && results.errors.length > 0 && (
                <p className="text-red-500"><strong>Chyby:</strong> {results.errors.length}</p>
              )}
            </div>
          </div>
        )}

        {/* Buttons */}
        <div className="flex justify-end space-x-3">
          <button
            onClick={onClose}
            className={`px-4 py-2 rounded-lg ${
              isDarkMode
                ? 'bg-gray-600 hover:bg-gray-500 text-white'
                : 'bg-gray-200 hover:bg-gray-300 text-gray-700'
            }`}
          >
            Zrušit
          </button>
          
          {!showPreview ? (
            <button
              onClick={generatePreview}
              disabled={!file || isProcessing}
              className={`px-4 py-2 rounded-lg font-medium ${
                !file || isProcessing
                  ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                  : 'bg-blue-500 hover:bg-blue-600 text-white'
              }`}
            >
              {isProcessing ? 'Načítám...' : '👀 Zobrazit náhled'}
            </button>
          ) : (
            <>
              <button
                onClick={() => setShowPreview(false)}
                className={`px-4 py-2 rounded-lg ${
                  isDarkMode
                    ? 'bg-gray-600 hover:bg-gray-500 text-white'
                    : 'bg-gray-300 hover:bg-gray-400 text-gray-700'
                }`}
              >
                ← Zpět
              </button>
              <button
                onClick={confirmImport}
                disabled={isProcessing}
                className={`px-4 py-2 rounded-lg font-medium ${
                  isProcessing
                    ? 'bg-gray-400 cursor-not-allowed text-gray-600'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {isProcessing ? 'Importuji...' : '✅ Potvrdit import'}
              </button>
            </>
          )}
        </div>

      </div>
    </div>
  );
}

export default SimpleCAImport;